import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import type { CSVValidationResult } from '../../types/asset-import.types';
import { assetImportService } from '../../services/asset-import.service';

interface CSVValidationResultsProps {
  readonly result: CSVValidationResult;
  readonly onImport: (assetIds: string[]) => void;
  readonly onCancel: () => void;
}

export default function CSVValidationResults({
  result,
  onImport,
  onCancel
}: Readonly<CSVValidationResultsProps>) {
  const [showAllRows, setShowAllRows] = useState(false);

  const handleExportErrors = async () => {
    try {
      const blob = await assetImportService.exportValidationErrors(result.errors);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'import-errors.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export errors:', error);
    }
  };

  const handleImport = () => {
    const assetIds = result.validAssets.map((asset) => asset.id);
    onImport(assetIds);
  };

  const displayAssets = showAllRows ? result.validAssets : result.previewAssets;

  return (
    <div className="space-y-6" data-testid="validation-results">
      {/* Summary */}
      {result.errorCount > 0 ? (
        <Alert variant="destructive" className="bg-amber-50 border-amber-200" data-testid="validation-error-alert">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-900">
            Validation Complete - {result.errorCount} error{result.errorCount !== 1 ? 's' : ''} found
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="bg-green-50 border-green-200" data-testid="validation-success-alert">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-900">
            Validation Passed - All {result.totalRows} rows are valid and ready to import
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Valid Rows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600" data-testid="valid-rows-count">{result.validRows}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive" data-testid="error-count">{result.errorCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Rows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="total-rows-count">{result.totalRows}</div>
          </CardContent>
        </Card>
      </div>

      {/* Errors Table */}
      {result.errorCount > 0 && (
        <Card data-testid="errors-table">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Errors to Resolve</CardTitle>
            <Button variant="outline" size="sm" onClick={handleExportErrors} data-testid="export-errors-btn">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Row</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.errors.map((error) => (
                    <TableRow key={`error-${error.row}-${error.field}`}>
                      <TableCell className="font-medium">{error.row}</TableCell>
                      <TableCell>{error.field}</TableCell>
                      <TableCell className="text-destructive">{error.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warnings Table - Financials field mismatches (optional to fix) */}
      {result.warningCount > 0 && (
        <Card data-testid="warnings-table">
          <CardHeader>
            <div className="flex flex-row items-center justify-between mb-2">
              <CardTitle className="text-orange-600">
                Warnings
              </CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              These fields don't match values in Financials. You can still import, but the data may not sync correctly.
            </p>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Row</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Warning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.warnings.map((warning, index) => (
                    <TableRow key={`warning-${warning.row}-${warning.field}-${index}`}>
                      <TableCell className="font-medium">{warning.row}</TableCell>
                      <TableCell>{warning.field}</TableCell>
                      <TableCell className="text-orange-700">{warning.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Table */}
      {result.validRows > 0 && (
        <Card data-testid="preview-table">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Preview</CardTitle>
            <span className="text-sm text-muted-foreground">
              Showing {displayAssets.length} of {result.validRows}
            </span>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayAssets.map((asset) => (
                    <TableRow key={asset.id || asset.assetCode}>
                      <TableCell className="font-medium">{asset.assetCode}</TableCell>
                      <TableCell>{asset.description}</TableCell>
                      <TableCell>{asset.category}</TableCell>
                      <TableCell className="text-right">
                        £{asset.boughtValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>{asset.boughtDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {!showAllRows && result.validRows > 5 && (
              <Button
                variant="link"
                className="mt-2"
                onClick={() => setShowAllRows(true)}
                data-testid="show-all-rows-btn"
              >
                Show all {result.validRows} rows
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          disabled={result.validRows === 0}
          data-testid="import-btn"
        >
          Import {result.validRows} {result.validRows === 1 ? 'Asset' : 'Assets'}
        </Button>
      </div>
    </div>
  );
}
